/**
 * プロフィールヘッダーの寸法と下地の作り方。
 *
 * **ここは本体(ProfilePage)とスケルトン(ProfileHeaderSkeleton)の共有点。**
 * 読み込み中と読み込み後で高さが1pxでも違うと切り替わりで下のコンテンツが飛ぶので、
 * 寸法は必ずこのモジュールから取ること。片方に数値を直書きしないこと。
 *
 * アバターはバナーの外に食い出さず、バナー内側に収める
 * (縦スペースを詰めるため。食い出させると後続要素がバナー下端を覆って
 *  クリックを奪う問題も出る)。
 */

// レイアウト側の上余白。打ち消す量を出すのに使う。
// StandardLayout の paddingTop と Container の py:4。**これらを変えたらここも合わせること。**
export const LAYOUT_TOP_PAD = { xs: 56, sm: 64 };
export const CONTAINER_PY = 32;

/**
 * バナーの引き上げ量。**PC と SP で方針が違う。**
 *
 * PC は AppBar の下まで潜り込ませて画面最上部から始める。
 * バナーが 270px あるので、上部が AppBar と重なっても見せ場は十分残る。
 *
 * **SP は潜り込ませない。** バナーが 200px しかないのに上端の 56px が
 * ロゴ・検索・アイコンと重なり、面積の3割近くが読めない絵になっていた。
 * Container の余白だけ打ち消して AppBar の直下から始める。
 *
 * ただし潜り込ませないだけだと画面最上部が素の暗色になって没入感が消えるので、
 * **SP では BannerTopWash（同じ絵をぼかした帯）を AppBar の裏に敷いて補う。**
 * AppBar は PC/SP どちらも透過のまま。塗るのはこのページ側の責任。
 */
export const BANNER_PULL_UP = {
  xs: `-${CONTAINER_PY}px`,
  sm: `-${LAYOUT_TOP_PAD.sm + CONTAINER_PY}px`,
};

// アバター〜名前の行より下にバナーを伸ばすぶん。紹介文の面の裏に潜り込み、
// PC ではさらに下まで伸ばして地の色へ溶かす「余韻」になる。
// モバイルは画面が狭く余韻を出す余地が無いので 16 固定。
// かつて compact(md:24) と選べるようにしていたが、コンパクトを選ぶ理由が無く廃止した。
const BANNER_EXTRA_DEPTH = { xs: 16, md: 150 };
// 余韻を含まない素の高さ。実際の高さ = これ + BANNER_EXTRA_DEPTH。
const BANNER_BASE_HEIGHT = { xs: 200, md: 270 };
// 余韻に関係なく紹介文の面が乗り上げる量。実際の乗り上げ = これ + depth。
// depth と同じ量だけ足すことで、バナーを下へ伸ばしても面の位置が動かない。
const BUBBLE_BASE_LIFT = { xs: 4, md: 12 };

// 高さ・乗り上げは depth から導出する。3つを手で揃えるとズレるので必ずここを通すこと。
export const banner = {
  extraDepth: BANNER_EXTRA_DEPTH,
  height: {
    xs: BANNER_BASE_HEIGHT.xs + BANNER_EXTRA_DEPTH.xs,
    md: BANNER_BASE_HEIGHT.md + BANNER_EXTRA_DEPTH.md,
  },
  bubbleOverlap: {
    xs: `${-(BUBBLE_BASE_LIFT.xs + BANNER_EXTRA_DEPTH.xs)}px`,
    md: `${-(BUBBLE_BASE_LIFT.md + BANNER_EXTRA_DEPTH.md)}px`,
  },
};

// AppBar の裏に潜るぶん(88/96)と depth は行に使えないので、
// 行が使える高さは BASE_HEIGHT - 88/96。
// 下端の行(アバター96 + 内側余白24*2 = 144px)がそこに収まる必要があるため、
// md は 270-96=174 で 30px ほど残している。BASE_HEIGHT はこれ以上下げられない。
// xs は FollowButton / SocialLinks をアイコン表示にして折り返しを無くしてあるので、
// 名前カラムは概ね 70px に収まりアバター(72)を超えない。200-88=112 に対して 104。
export const AVATAR_SIZE = { xs: 72, md: 96 };

// バナー下端の1行の内側余白。スケルトンでもアバターの位置を揃えるのに使う。
export const BANNER_ROW_PY = { xs: 2, md: 3 };
export const BANNER_ROW_PX = 1.5;
// Container の px を打ち消してバナーを全幅に広げる量。BANNER_ROW_PX と対で使う。
export const BANNER_BLEED_MX = -1.5;

/**
 * 紹介文の面。
 *
 * かつてアバターを指すしっぽを付けていたが廃止した。しっぽが別要素だったせいで
 * 「ベタ塗りのみ・枠線なし」(継ぎ目で濃さが食い違うため)という縛りがあったが、
 * それも一緒に無くなっている。
 *
 * accent が null なら無彩色。単色を全面に敷くと平坦になり、かといって
 * 全面に色を回すと派手なので、**左上を光源にして角から減衰させる**。
 * アバターが左上にあるので、光の向きが自然につながる。
 */
const BUBBLE_BG = 'rgba(255, 255, 255, 0.03)';
export const bubbleSurface = (accent: string | null) => ({
  backgroundColor: BUBBLE_BG,
  backgroundImage: accent
    ? `radial-gradient(120% 150% at 0% 0%, ${accent}26 0%, ${accent}0b 40%, transparent 70%)`
    : 'none',
  boxShadow: [
    // 上辺のヘアライン。ガラスの縁に光が乗った感じを作る。面の色より効く
    `inset 0 1px 0 ${accent ? `${accent}3d` : 'rgba(255,255,255,0.10)'}`,
    '0 4px 20px rgba(0, 0, 0, 0.35)',
  ].join(', '),
});
// 乗り上げ量は上の banner が導出する。
// BUBBLE_BASE_LIFT の 4/12 は、面の上辺がアバターの下端にぶつからない上限。
// アバター下端はバナーの「行の下端」から内側余白ぶん上(xs:16px / md:24px)。

// バナー上に載せる操作系の共通スタイル。背景がユーザーごとに変わるので、
// 何色の上でも読めるよう半透明の黒＋ブラーで下地を作る。
export const GLASS_ACTION_SX = {
  color: '#fff',
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
} as const;

// バナー上のアイコンボタン。モバイルでは一段小さくする。
export const HEADER_ICON_SX = {
  ...GLASS_ACTION_SX,
  p: { xs: '4px', md: '6px' },
  '& .MuiSvgIcon-root': { fontSize: { xs: '1rem', md: '1.15rem' } },
} as const;

// 下端の文字・操作を読ませるスクリム。別要素にすると border-radius を継承できず
// 四角いまま角丸を塗り潰してしまうので、バナー自身の背景レイヤーとして重ねる。
//
// スクリムとフェードの stop は「バナー高さに対する％」なので、md は余韻(150px)を
// 足したぶん位置がズレる。そのため xs / md で別々に持つ。
// md の値は xs の px 位置を 150px ぶん上へずらして％に直したもの。
// (例: xs 35% × 294px = 103px → +126px = 229px → 420px の 54.5%)
// BANNER_EXTRA_DEPTH を変えたらこの％も引き直すこと。
// base は xs と、md の compact(高さ294)で共用する。
// mdExtended は base の px 位置を上へずらして 420px 基準の％へ直したもの。
// 下から上へ必ず単調に薄くすること。途中にピークを作ると下端の消え方と打ち消し合って
// 下地の見え方が反転し、そこが線に見える(以前 30% にピークがあり境界線が出ていた)。
const BANNER_SCRIM = {
  base: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 35%, transparent 65%)',
  mdExtended: 'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.74) 30%, rgba(0,0,0,0.35) 54.5%, transparent 76%)',
};

// 写真は生成グラデ(明度12〜18%)よりずっと明るいので、同じスクリムでは文字が読めない。
// 読みにくければ stop を濃くする。単調性の制約は BANNER_SCRIM と同じ。
const BANNER_IMAGE_SCRIM = {
  base: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.62) 25%, rgba(0,0,0,0.28) 50%, transparent 78%)',
  mdExtended: 'linear-gradient(to top, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.88) 30%, rgba(0,0,0,0.62) 47%, rgba(0,0,0,0.28) 65%, transparent 85%)',
};

// 下端を消して地との境目を無くす。
// **地の色を上から塗るのではなく、マスクでバナー自身を透明にすること。**
// 以前は #121212 のベタ塗りを重ねていたが、それだと「地が単色である」前提に縛られ、
// 環境光(AmbientBackground)を敷いた瞬間に境目が帯として出る。
// マスクなら地が何であっても成立する。角丸とは両立しない（切り口が見えなくなる）。
const BANNER_MASK = {
  base: 'linear-gradient(to top, transparent 0%, #000 14%)',
  mdExtended: 'linear-gradient(to top, transparent 0%, #000 40%)',
};

// 下端の消え方。スクリムと同じ理由でブレークポイント別に持つ。
export const BANNER_MASK_SX = { xs: BANNER_MASK.base, md: BANNER_MASK.mdExtended };

/**
 * 画像未設定時のヘッダー。
 *
 * **アクセント色を明示的に設定している人だけ、その色相に合わせる。**
 * 未設定の人はユーザー名から決定的に色相を導く（＝実質ランダム）。
 *
 * 全員をアクセント色に合わせないのは、既定がライムなので**未設定の人が全員同じ**に
 * なってしまうため。ユーザー名シードは「画像を出していない人でも他人と違って見える」
 * ための仕掛けなので、それを潰さない。
 *
 * accentHue を渡した場合もシードは捨てない。副色への振り幅をシードで散らすので、
 * 同じアクセントを選んだ人同士でも同じ絵にはならない。
 */
export const bannerGradient = (seed: string, accentHue?: number): string => {
  let n = 0;
  for (let i = 0; i < seed.length; i++) {
    n = (n * 31 + seed.charCodeAt(i)) % 360;
  }
  const hue = accentHue ?? n;
  // 副色への振り幅。従来は +45° 固定だったが、アクセント基準だと全員同じ関係になるので
  // シードで 28〜72° に散らす（同系色の範囲は超えない）
  const spread = 28 + (n % 45);
  return [
    `radial-gradient(120% 140% at 15% 0%, hsl(${hue} 55% 20% / 0.9) 0%, transparent 60%)`,
    `linear-gradient(135deg, hsl(${hue} 45% 17%) 0%, hsl(${(hue + spread) % 360} 40% 12%) 55%, rgba(0,0,0,0.92) 100%)`,
  ].join(', ');
};

/**
 * 誰のものでもない下地。**読み込み中に使う。**
 *
 * かつてはこの間もユーザー名シードのグラデを描いていたが、シードが空文字なので
 * 色相 0(赤)に固定され、データが来た瞬間に本人の色相へ飛んでいた。
 * SP は BannerTopWash が読み込み中も出るので、その飛びが画面最上部にも出ていた。
 * 誰の色でもない無彩色から始めれば、確定後のフェードインが「現れる」に見える。
 */
export const NEUTRAL_BANNER_BASE =
  'linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 45%, rgba(0,0,0,0.28) 100%)';

// 下地。縦は必ず top 基準。center にすると切り落としが上下等分になり、
// depth を変えたときに画像の上端まで動いてしまう(横は cover で切れるので center)。
// BannerDistortion もこれと同じ切り方を再現している。片方だけ変えないこと。
export const bannerBaseSx = (imageUrl: string | null, seed: string, accentHue?: number): string =>
  imageUrl ? `url("${imageUrl}") center top / cover no-repeat` : bannerGradient(seed, accentHue);

// 上に重ねる層。extended は md だけ下へ長く伸ばすので単一の文字列では表現できない。
// モバイルは depth を伸ばさないので常に base。
export const bannerOverlaySx = (imageUrl: string | null) => {
  const scrim = imageUrl ? BANNER_IMAGE_SCRIM : BANNER_SCRIM;
  return { xs: scrim.base, md: scrim.mdExtended };
};

// サーバー(BannerUploadView.TARGET_SIZE)の出力と必ず同じ値にすること。
// クライアント側で先にこの寸法へ切ることで、プレビューと保存後の見え方が一致する。
export const BANNER_OUTPUT = { width: 1280, height: 540 };

/**
 * アイコンの保存サイズ。**サーバー(AvatarUploadView.TARGET_SIZE)と同じ値にすること。**
 * 同じ寸法で中央切り出しまで済ませてからプレビューするので、
 * ここで見えているものと保存後の見え方が一致する。
 */
export const AVATAR_OUTPUT = { width: 200, height: 200 };
